import React, { useState } from 'react';

/**
 * Dropdown to pick a company under research, plus an inline "+ Add new"
 * affordance that prompts for name and ticker.
 *
 * Props:
 *   companies        Array<{company_id, name, ticker}>
 *   selectedId       string | null
 *   onSelect(id)
 *   onCreate({name, ticker})  Promise<created>
 *   onDelete(companyId)       Promise<void> (optional)
 *   rightActions             ReactNode (optional)
 */
export default function CompanySelector({ companies, selectedId, onSelect, onCreate, onDelete, rightActions = null }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTicker, setNewTicker] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedCompany = companies.find((c) => c.company_id === selectedId);

  const handleAdd = async () => {
    if (!newName.trim()) {
      setError('Company name is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await onCreate({ name: newName.trim(), ticker: newTicker.trim() || undefined });
      setNewName('');
      setNewTicker('');
      setAdding(false);
      if (created?.company_id) {
        onSelect(created.company_id);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to add company');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId || !onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(selectedId);
      setConfirmDelete(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to delete company');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (adding) {
    return (
      <div className="card p-3 mb-3">
        <h6 className="mb-2">Add a new company</h6>
        <div className="row g-2">
          <div className="col-md-7">
            <input
              className="form-control"
              placeholder="Company name (e.g. Upstart Holdings)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control"
              placeholder="Ticker (optional)"
              value={newTicker}
              onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
            />
          </div>
          <div className="col-md-2 d-flex gap-2">
            <button className="btn btn-primary flex-fill" onClick={handleAdd} disabled={busy}>
              {busy ? '...' : 'Add'}
            </button>
            <button
              className="btn btn-link"
              onClick={() => { setAdding(false); setError(null); }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
        {error && <div className="text-danger small mt-2">{error}</div>}
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <label className="form-label mb-0 fw-bold">Company:</label>
        <select
          className="form-select"
          style={{ maxWidth: 360 }}
          value={selectedId || ''}
          onChange={(e) => onSelect(e.target.value || null)}
        >
          <option value="">— Select a company —</option>
          {companies.map((c) => (
            <option key={c.company_id} value={c.company_id}>
              {c.name}{c.ticker ? ` (${c.ticker})` : ''}
            </option>
          ))}
        </select>
        <button className="btn btn-outline-primary" onClick={() => setAdding(true)}>
          <i className="bi bi-plus-lg me-1" />Add company
        </button>
        {onDelete && selectedId && (
          <button
            className="btn btn-outline-danger"
            onClick={() => setConfirmDelete(true)}
            disabled={deleting}
            title="Delete this company and all its data"
          >
            <i className="bi bi-trash me-1" />Delete company
          </button>
        )}
        <div className="ms-auto">
          {rightActions}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-2">{error}<button type="button" className="btn-close float-end" aria-label="Close" onClick={() => setError(null)} /></div>}

      {confirmDelete && selectedCompany && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-exclamation-triangle me-2" />Delete Company
                </h5>
                <button className="btn-close" onClick={() => setConfirmDelete(false)} disabled={deleting} />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to permanently delete <strong>{selectedCompany.name}{selectedCompany.ticker ? ` (${selectedCompany.ticker})` : ''}</strong>?
                </p>
                <p className="text-danger mb-0">
                  This will remove all associated files, earnings data, and Redshift records. <strong>This action cannot be undone.</strong>
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
                  {deleting ? <><span className="spinner-border spinner-border-sm me-2" />Deleting…</> : 'Delete permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
